// ─────────────────────────────────────────────────────────────────────────────
// 🚨 A BORROWER IN THE GUIDED FLOW DOES NOT LAND ON THE DASHBOARD.
//
// Jesse: "a borrower with GUIDED_FLOW_ENABLED and not yet approved is routed to
// the shell from login and from any dashboard deep link. Approved borrowers
// never see it."
//
// 🔑 THE SERVER DECIDES, NOT THE CLIENT. /api/guided-flow/state answers
// `shouldEnter` from the flag AND the two fields that define approval —
// users.application_status and users.atlas_eligibility_status. A client-side
// reading of either would be a borrower-visible route decision made from a
// cached user object, and the flag would be a build-time constant rather than an
// environment one.
//
// ⚠️ IT WRAPS THE DESTINATION, NOT THE LOGIN. Gating at login only catches the
// login path; "any dashboard deep link" means a borrower who opens
// /documents from an email is redirected too, and the only place that is true
// for every entry point is the destination route itself.
//
// 🚨 IT SHOWS A NEUTRAL LOADING SCREEN UNTIL IT KNOWS (Jesse, 2026-10-19 — reverses the earlier "render the
// children while it asks"). Rendering the page while asking flashed the classic application and the dashboard
// before the shell, on reload, on deep links and on a native cold start. Now nothing of the page renders — no
// classic-flow content, no tappable control — until the server has answered. To keep the wait to the first
// navigation of a session, the answer is remembered (per token) and re-asked in the background.
//
// 🔑 ONE RULE FOR EVERY APP ROUTE (Jesse, 2026-10-19): until the step-8 decision, a guided borrower on any app route
// lands on its guided-shell version or on the current step — never in the dashboard app shell:
//   /documents (and /portal/documents) → /apply/guided/documents   (hash kept: #action-needed)
//   /messages                          → /apply/guided/messages    (query kept: ?conversation=)
//   /settings                          → /apply/guided/account
//   everything else (/dashboard, /alerts, /apply, /verification, /homes, …) → /apply/guided (the current step)
// The old one-navigation "fromGuided" exception is gone: nothing in the shell exits to the dashboard any more.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

// The last answer for this token, so a second navigation in the session does not wait (re-asked in the background).
let remembered = { token: null, shouldEnter: null, homesOpen: false };
export const _forgetGuidedDecision = () => { remembered = { token: null, shouldEnter: null, homesOpen: false }; };
// 🔑 Homes stay browsable to an applicant who has signed and waits on another (Part 1 item 1): /homes and /homes/:id.
export const isHomesPath = (pathname) => /^\/homes(\/|$)/.test(String(pathname || ''));
/** The shell keeps the remembered answer current from every state check it makes (a decision or a rollback flips it). */
export const rememberGuidedDecision = (token, shouldEnter) => {
  if (token) remembered = { token, shouldEnter: !!shouldEnter, homesOpen: remembered.token === token ? remembered.homesOpen : false };
};

/** Where an app route goes for a borrower in the guided flow. Pure — the test reads it. */
export function guidedDestination(pathname, search = '', hash = '') {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  if (p === '/documents' || p === '/portal/documents') return `/apply/guided/documents${hash || ''}`;
  if (p === '/messages') return `/apply/guided/messages${search || ''}`;
  if (p === '/settings') return '/apply/guided/account';
  return '/apply/guided';
}

/** The neutral screen shown until the guard knows: no page content, nothing to tap. */
export function GuidedDecisionPending() {
  return (
    <div data-testid="guided-decision-pending" aria-busy="true"
      style={{ minHeight: '100vh', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid #e5e7eb', borderTopColor: '#3a6ad9',
        animation: 'doorly-spin 0.9s linear infinite' }} />
      <style>{'@keyframes doorly-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

export default function RequireGuidedFlow({ children }) {
  const location = useLocation();
  // 🚨 THE TOKEN COMES FROM THE AUTH CONTEXT (it once read the wrong localStorage key and never redirected anyone).
  const { token } = useAuth();
  // ⚠️ A NAVIGATION THE SHELL MADE AFTER THE DECISION (step 8's "view your SmartEstimate / notice", state.fromGuided)
  // never trusts the remembered answer: the decision has just closed the shell, and the remembered "guided" would send
  // the borrower straight back. It waits for the server instead (2026-10-19, e2e approved-smartestimate).
  const freshAsk = !!(location.state && location.state.fromGuided);
  const known = !freshAsk && remembered.token === token ? remembered.shouldEnter : null;
  const [shouldEnter, setShouldEnter] = useState(token ? known : false);   // null = unknown yet

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { if (!cancelled) setShouldEnter(false); return; }
      try {
        const res = await axios.get(`${API}/api/guided-flow/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const v = !!res.data?.shouldEnter;
        remembered = { token, shouldEnter: v, homesOpen: !!res.data?.homesOpen };
        if (!cancelled) setShouldEnter(v);
      } catch (e) {
        // ⚠️ AN UNANSWERABLE QUESTION LEAVES THE BORROWER WHERE THEY ARE — unless this session already knows they are
        // guided, in which case the remembered answer stands (a blip must not drop them into the dashboard).
        if (!cancelled) setShouldEnter(remembered.token === token && remembered.shouldEnter === true);
      }
    })();
    return () => { cancelled = true; };
    // ⚠️ token IS A DEPENDENCY: it arrives from the context on the first render after a login.
  }, [location.pathname, token]);

  if (shouldEnter === null) return <GuidedDecisionPending />;
  if (shouldEnter === true && !(remembered.token === token && remembered.homesOpen && isHomesPath(location.pathname))) {
    return <Navigate to={guidedDestination(location.pathname, location.search, location.hash)} replace />;
  }
  return children;
}
